import apiClient, { type ApiError } from './apiClient'
import type {
  ResidentApplicationData,
  ResidentApplicationResponse,
  UpdateApplicationRequest,
  AwaitingCategoryData,
} from '@/types/application'
import { getStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from './config'

/**
 * Transform application data to populate property object with additional fields
 * @param data - Application data from API
 * @returns Transformed application data with populated property fields
 */
/**
 * Parse awaiting_reasons JSON string from API into awaiting_data and optional notes
 * Matches Angular: JSON.parse -> extract .awaiting_data if present; preserves .notes when present
 */
function parseAwaitingReasons(reasonsJson: string | undefined): {
  awaiting_data?: AwaitingCategoryData,
  awaiting_notes?: string
} {
  if (!reasonsJson || typeof reasonsJson !== 'string') return {}
  try {
    const parsed = JSON.parse(reasonsJson) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return {}

    const awaiting_notes =
      typeof parsed.notes === 'string' && parsed.notes.trim() !== '' ? parsed.notes : undefined

    if (parsed.awaiting_data && typeof parsed.awaiting_data === 'object') {
      return { awaiting_data: parsed.awaiting_data as AwaitingCategoryData, awaiting_notes }
    }

    const { notes: _omit, ...rest } = parsed as { notes?: string } & AwaitingCategoryData
    if (Object.keys(rest).length === 0) return { awaiting_notes }
    return { awaiting_data: rest as AwaitingCategoryData, awaiting_notes }
  } catch {
    return {}
  }
}

function transformApplicationData(data: ResidentApplicationData): ResidentApplicationData {
  if (!data) return data

  // Ensure property object exists
  if (!data.property) {
    data.property = {}
  }

  // Populate lease_duration from root level
  if (data.lease_duration && !data.property.lease_duration) {
    data.property.lease_duration = data.lease_duration
  }

  // Populate email and mobile_no from agent object
  if (data.agent) {
    if (data.agent.email && !data.property.email) {
      data.property.email = data.agent.email
    }
    if (data.agent.mobile_no && !data.property.mobile_no) {
      data.property.mobile_no = data.agent.mobile_no
    }
  }

  // Parse awaiting_reasons JSON into awaiting_data / awaiting_notes (AWAITING workflow)
  if (data.awaiting_reasons) {
    const { awaiting_data: parsedAwaiting, awaiting_notes } = parseAwaitingReasons(
      data.awaiting_reasons
    )
    if (!data.awaiting_data && parsedAwaiting) {
      data.awaiting_data = parsedAwaiting
    }
    if (awaiting_notes) {
      data.awaiting_notes = awaiting_notes
    }
  }

  return data
}

/**
 * Get all resident applications for the current user
 * @param sortBy - Field to sort by (e.g., 'lease_start_date')
 * @param sortOrder - Sort direction ('asc' or 'desc')
 * @returns Array of resident applications
 * @throws ApiError if request fails
 */
export async function getResidentApplications(
  sortBy?: string,
  sortOrder?: string
): Promise<ResidentApplicationData[]> {
  // Deduplicate concurrent requests for all applications
  // If multiple components call this at the same time, return the same pending promise
  // to avoid hammering the API.
  const cache = getResidentApplications as typeof getResidentApplications & {
    _pendingPromise?: Promise<ResidentApplicationData[]>
  }
  if (cache._pendingPromise) {
    try {
      return await cache._pendingPromise
    } catch {
      // If the pending promise failed, continue to issue a new request below
    }
  }

  const pending: Promise<ResidentApplicationData[]> = (async () => {
    try {
      // Build query params if sort parameters are provided
      const params = new URLSearchParams()
      if (sortBy) {
        params.append('sort_by', sortBy)
      }
      if (sortOrder) {
        params.append('sort_order', sortOrder)
      }

      const url = params.toString()
        ? `${API_ENDPOINTS.RESIDENT_APPLICATION}?${params.toString()}`
        : API_ENDPOINTS.RESIDENT_APPLICATION

      const response = await apiClient.get<
        ResidentApplicationResponse | ResidentApplicationData | ResidentApplicationData[]
      >(url)

      const responseData = response.data

      // Handle different response structures
      if (Array.isArray(responseData)) {
        return responseData.map(transformApplicationData)
      }

      if (responseData && typeof responseData === 'object') {
        // Check if it's wrapped in a data property
        if ('data' in responseData) {
          const wrappedResponse = responseData as ResidentApplicationResponse
          if (Array.isArray(wrappedResponse.data)) {
            return wrappedResponse.data.map(transformApplicationData)
          }
          if (wrappedResponse.data) {
            return [transformApplicationData(wrappedResponse.data as ResidentApplicationData)]
          }
          return []
        }

        // Single application object
        return [transformApplicationData(responseData as ResidentApplicationData)]
      }

      return []
    } catch (error) {
      if (isAuthError(error)) {
        throw createAuthError()
      }

      throw {
        message: extractErrorMessage(error, 'Failed to fetch resident applications.'),
        status: (error as ApiError).status,
      } as ApiError
    }
  })()

  cache._pendingPromise = pending

  try {
    return await pending
  } finally {
    // Clear pending promise when finished so subsequent calls can refetch
    delete cache._pendingPromise
  }
}

/**
 * Get resident application by ID
 * @param applicantId - Applicant ID
 * @returns Resident application data
 * @throws ApiError if request fails
 */
export async function getResidentApplicationById(
  applicantId: string | number
): Promise<ResidentApplicationData> {
  try {
    const response = await apiClient.get<
      ResidentApplicationResponse | ResidentApplicationData
    >(API_ENDPOINTS.RESIDENT_APPLICATION_BY_ID(applicantId))

    const responseData = response.data

    // Handle different response structures
    if (responseData && typeof responseData === 'object') {
      // Check if it's wrapped in a data property
      if ('data' in responseData && !('applicant_id' in responseData)) {
        const wrappedResponse = responseData as ResidentApplicationResponse
        if (Array.isArray(wrappedResponse.data)) {
          return transformApplicationData(wrappedResponse.data[0] || ({} as ResidentApplicationData))
        }
        return transformApplicationData((wrappedResponse.data as ResidentApplicationData) || ({} as ResidentApplicationData))
      }

      // Direct application object
      return transformApplicationData(responseData as ResidentApplicationData)
    }

    return {} as ResidentApplicationData
  } catch (error) {
    const apiError = error as ApiError

    // Check for auth errors
    if (apiError.status === 401 || apiError.status === 403) {
      throw {
        message: 'Your session has expired. Please log in again to continue.',
        status: apiError.status,
        isAuthError: true,
      } as ApiError & { isAuthError: boolean }
    }

    throw {
      message: apiError.message || 'Failed to fetch application data.',
      status: apiError.status,
    } as ApiError
  }
}

/**
 * Update user's selected invite (select one application; backend auto-cancels other active invites).
 * Used when user has multiple active invites and selects one from the dashboard.
 * @param invite - Selected invite/application object (from resident_application list)
 * @throws ApiError if request fails
 */
export async function updateUserInvite(invite: ResidentApplicationData): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.UPDATE_USER_INVITE, invite)
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to update selected invite.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Update resident application
 * @param applicantId - Applicant ID
 * @param updateData - Data to update
 * @returns Updated application data
 * @throws ApiError if request fails
 */
export async function updateResidentApplication(
  applicantId: string | number,
  updateData: UpdateApplicationRequest
): Promise<ResidentApplicationData> {
  try {
    // Get token for additional header
    const token = getStorageItem(STORAGE_KEYS.authToken)

    const response = await apiClient.post<ResidentApplicationResponse>(
      API_ENDPOINTS.RESIDENT_APPLICATION_BY_ID(applicantId),
      updateData,
      {
        headers: {
          'Accept-Language': 'en-US,en;q=0.9',
          ...(token && { token }), // Additional token header as per API docs
        },
      }
    )

    const responseData = response.data

    // Handle response
    if (responseData && responseData.success === false) {
      throw {
        message: responseData.message || 'Failed to update application.',
        status: 400,
      } as ApiError
    }

    // Extract updated data
    if (responseData.data) {
      if (Array.isArray(responseData.data)) {
        return transformApplicationData(responseData.data[0] || ({} as ResidentApplicationData))
      }
      return transformApplicationData(responseData.data as ResidentApplicationData)
    }

    // Return the request data as confirmation (with transformation)
    return transformApplicationData(updateData as unknown as ResidentApplicationData)
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to update application.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Reclassify a saved job between current and previous employment.
 * Mirrors Angular `ResidentApplicationAddComponent.makeJobConfirm`:
 * POST /api/previous_application with { applicant_id, job_id, type }.
 * `type: 'previous'` (current -> previous) requires `jobEndDate`;
 * `type: 'current'` (previous -> current) omits it. Persists server-side
 * immediately, independent of the tab's whole-form Save.
 */
export async function makeJobPreviousOrCurrent(params: {
  applicantId: string | number
  jobId: string | number
  type: 'previous' | 'current'
  jobEndDate?: string
}): Promise<void> {
  const { applicantId, jobId, type, jobEndDate } = params
  try {
    const requestData: Record<string, unknown> = {
      applicant_id: applicantId,
      job_id: jobId,
      type,
    }
    if (jobEndDate) {
      requestData.job_end_date = jobEndDate
    }

    const response = await apiClient.post<ResidentApplicationResponse>(
      API_ENDPOINTS.PREVIOUS_APPLICATION,
      requestData
    )

    const responseData = response.data
    if (responseData && responseData.success === false) {
      throw {
        message: responseData.message || 'Failed to update job.',
        status: 400,
      } as ApiError
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to update job.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Check if application has an unpaid application fee invoice (blocks Submit for Processing)
 * Based on workflow: "If application fee invoice exists and is unpaid, show error and do not submit."
 * @param applicationData - Application data
 * @returns True if applicant should be blocked from submitting for processing
 */
export function hasUnpaidApplicationFeeInvoice(
  applicationData: ResidentApplicationData | null | undefined
): boolean {
  if (!applicationData) return false
  const hasInvoice =
    applicationData.application_fee_invoice === true ||
    applicationData.application_fee_invoice === '1' ||
    applicationData.application_fee_invoice === 1
  if (!hasInvoice) return false
  const balanceDue = applicationData.balance_due != null ? Number(applicationData.balance_due) : 0
  return balanceDue > 0
}

/**
 * Submit application for processing (AWAITING workflow)
 * POST /api/resident_application/{applicantId} with request_approval: true
 * When progress is "Awaiting External" and no changes: include data_not_change: "1"
 * @param applicantId - Applicant ID
 * @param dataNotChange - Set to true when progress is "Awaiting External" and no changes
 * @returns Updated application data
 * @throws ApiError if request fails or application has unpaid application fee invoice
 */
export async function requestApprovalToProcess(
  applicantId: string | number,
  dataNotChange?: boolean
): Promise<ResidentApplicationData> {
  const payload: UpdateApplicationRequest = {
    request_approval: true,
    ...(dataNotChange && { data_not_change: '1' }),
  }
  return updateResidentApplication(applicantId, payload)
}

/**
 * Upload proof of income files (general Proof of Income section – README §6.2, §6.3)
 * POST /api/resident_application/{applicantId} with multipart form-data: proof_of_income[0], proof_of_income[1], ...
 * Max 6 files total, 10 MB per file when AWAITING else 5 MB.
 * @param applicantId - Applicant ID
 * @param files - One or more files to upload
 * @returns Updated application data (response may include proof_of_income array)
 */
export async function uploadProofOfIncomeFiles(
  applicantId: string | number,
  files: File | File[]
): Promise<ResidentApplicationData> {
  const token = getStorageItem(STORAGE_KEYS.authToken)
  const formData = new FormData()
  const fileArray = Array.isArray(files) ? files : [files]
  fileArray.forEach((file, index) => {
    formData.append(`proof_of_income[${index}]`, file)
  })

  const response = await apiClient.post<ResidentApplicationResponse>(
    API_ENDPOINTS.RESIDENT_APPLICATION_BY_ID(applicantId),
    formData,
    {
      headers: {
        'Accept-Language': 'en-US,en;q=0.9',
        ...(token && { token }),
        // Do not set Content-Type; browser sets multipart/form-data with boundary
      },
    }
  )

  const responseData = response.data
  if (responseData && responseData.success === false) {
    throw {
      message: (responseData as { message?: string }).message || 'Failed to upload proof of income.',
      status: 400,
    } as ApiError
  }
  if (responseData?.data) {
    const data = Array.isArray(responseData.data) ? responseData.data[0] : responseData.data
    return transformApplicationData((data as ResidentApplicationData) || ({} as ResidentApplicationData))
  }
  return {} as ResidentApplicationData
}

/**
 * Delete proof of income file(s) by attachment ID (README §6.5)
 * POST /api/resident_application/{applicantId} with body: { deleted: { proof_of_income: [id, ...] } }
 */
export async function deleteProofOfIncomeFiles(
  applicantId: string | number,
  attachmentIds: (number | string)[]
): Promise<ResidentApplicationData> {
  const payload: UpdateApplicationRequest = {
    deleted: { proof_of_income: attachmentIds },
  } as UpdateApplicationRequest
  return updateResidentApplication(applicantId, payload)
}

export interface CreditBureauConfig {
  active?: string
  details?: {
    config_value?: string
    cb_addressline1?: string
    cb_city?: string
    cb_state?: string
    cb_zipcode?: string
    cb_telephone?: string
    cb_fax?: string
  }
}

export async function fetchCreditBureauActive(): Promise<CreditBureauConfig | null> {
  try {
    const response = await apiClient.get<CreditBureauConfig>(API_ENDPOINTS.CREDIT_BUREAU_ACTIVE)
    return response.data || null
  } catch {
    return null
  }
}

export async function fetchCreditBureauDetails(
  applicantId: string | number
): Promise<CreditBureauConfig | null> {
  try {
    const response = await apiClient.get<CreditBureauConfig>(
      API_ENDPOINTS.CREDIT_BUREAU_DETAILS(applicantId)
    )
    return response.data || null
  } catch {
    return null
  }
}
